import { ref } from 'vue'
import { defineStore } from 'pinia'
import * as ApiService from '@/core/services/ApiService'
import { usePrimaryComplaintStore } from '@/stores/primaryComplaint'
import { useTaskStore } from '@/stores/task'
import { logError } from '@/utils/safeErrorLog'

const FACILITY_KEY = import.meta.env.VITE_APP_FACILITY_KEY ?? null

/** Facility key: selected patient first (from GetUserPatients), then link (magic link), store, env (build-time), or facilityID fallback for legacy. */
export function getFacilityKey(): string {
  const state = useProfileStore()
  const fromPatient = state.currentPatient
  if (fromPatient?.facilityKey && String(fromPatient.facilityKey).trim())
    return String(fromPatient.facilityKey)
  const fromLink = state.linkInfo?.facilityKey
  if (fromLink && String(fromLink).trim()) return String(fromLink)
  const fromStore = state.facilityKey
  if (fromStore && String(fromStore).trim()) return String(fromStore)
  const fromEnv = import.meta.env.VITE_APP_FACILITY_KEY
  if (fromEnv && String(fromEnv).trim()) return String(fromEnv)
  const fromPatientId = state.currentPatient?.facilityID
  return fromPatientId != null ? String(fromPatientId) : ''
}

/** Facility key for Portal API only. Prefer selected patient (GetUserPatients), then link, store, env. Never returns facilityID. */
export function getFacilityKeyForPortal(): string {
  const state = useProfileStore()
  const fromPatient = state.currentPatient
  if (fromPatient?.facilityKey && String(fromPatient.facilityKey).trim())
    return String(fromPatient.facilityKey)
  const fromLink = state.linkInfo?.facilityKey
  if (fromLink && String(fromLink).trim()) return String(fromLink)
  const fromStore = state.facilityKey
  if (fromStore && String(fromStore).trim()) return String(fromStore)
  const fromEnv = import.meta.env.VITE_APP_FACILITY_KEY
  if (fromEnv && String(fromEnv).trim()) return String(fromEnv)
  return ''
}

/** Patient key for Portal API. From selected patient (GetUserPatients), then link, else patientID as string. */
export function getPatientKey(): string {
  const state = useProfileStore()
  const fromLink = state.linkInfo?.patientKey
  if (fromLink && String(fromLink).trim()) return String(fromLink)
  const p = state.currentPatient
  if (p?.patientKey && String(p.patientKey).trim()) return String(p.patientKey)
  return p?.patientID != null ? String(p.patientID) : ''
}

export interface FacilityInfo {
  officeName: string
  address: string
  address2: string
  city: string
  state: string
  zip: string
  phone: string
  openEdge: boolean
  fortis: boolean
}

interface LinkInfo {
  expired: boolean
  facilityID: number
  facilityKey: string
  patientID: number
  patientKey: string
  taskID: number
  taskType: string
  userExists: boolean
}

export interface AuthInfo {
  firstName: string
  lastName: string
  nickName: string
  email: string
  [key: string]: unknown
}

export interface Patient {
  id: number
  entryDate: string
  patientPortalUserID: number
  facilityID: number
  patientID: number
  /** From GetUserPatients; use for Portal API (facilityKey/patientKey). */
  facilityKey?: string
  /** From GetUserPatients; use for Portal API. */
  patientKey?: string
  firstName: string
  lastName: string
  authRep: boolean
  officeName: string
  /** From API / patient lookup when available; used e.g. for health history (female-specific questions). */
  sex?: string
  /** From API / patient lookup when available; used e.g. for health history year ranges. */
  dob?: string
}

/**
 * The signed-in portal user, their patients, the selected patient, and the magic-link info.
 * Persisted whole under localStorage `portal-profile` (zustand format, version 0), like React.
 */
export const useProfileStore = defineStore(
  'profile',
  () => {
    const facilityKey = ref<string | null>(FACILITY_KEY)
    const intakeKey = ref<string | null>(null)
    const profileAuthInfo = ref<AuthInfo | null>(null)
    const patientAssociated = ref<Patient[] | null>(null)
    const currentPatient = ref<Patient | null>(null)
    const facilityInfo = ref<FacilityInfo | null>(null)
    const linkInfo = ref<LinkInfo | null>(null)
    const currentProfilePageView = ref<string | null>(null)
    const currentProfilePaymentView = ref<string>('PaymentCardList')

    function setFacilityKey(key: string | null) {
      facilityKey.value = key
    }

    function setIntakeKey(key: string | null) {
      intakeKey.value = key
    }

    function setProfileAuthInfo(info: AuthInfo | null) {
      profileAuthInfo.value = info
    }

    function setProfileMyInfo(payload: Partial<AuthInfo>) {
      const current = profileAuthInfo.value
      if (!current) return
      profileAuthInfo.value = {
        ...current,
        firstName: (payload.firstName ?? current.firstName) as string,
        lastName: (payload.lastName ?? current.lastName) as string,
        nickName: (payload.nickName ?? current.nickName) as string,
        email: (payload.email ?? current.email) as string
      }
    }

    function setPatientAssociated(patients: Patient[] | null) {
      patientAssociated.value = patients
    }

    /** Selects a patient, and clears the case history and complaint flow of the previous one. */
    function setCurrentPatient(patient: Patient | null) {
      currentPatient.value = patient
      useTaskStore().resetCaseHistory()
      usePrimaryComplaintStore().startNewComplaint()
    }

    /** Merge fields into the selected patient without resetting case history / complaint flow (e.g. after demographics save). */
    function patchCurrentPatient(partial: Partial<Patient>) {
      if (!currentPatient.value) return
      currentPatient.value = { ...currentPatient.value, ...partial }
    }

    function setFacilityInfo(info: FacilityInfo | null) {
      facilityInfo.value = info
    }

    function setLinkInfo(info: LinkInfo | null) {
      linkInfo.value = info
    }

    function setProfileCurrentPage(view: string | null) {
      currentProfilePageView.value = view
    }

    function setProfilePaymentCurrentPage(view: string) {
      currentProfilePaymentView.value = view
    }

    async function getLink(key: string): Promise<LinkInfo | null> {
      try {
        const response = await ApiService.get(`/PatientPortal/GetLink/${encodeURIComponent(key)}`)
        const data = (response.data as LinkInfo) ?? null
        linkInfo.value = data
        return data
      } catch (err) {
        logError('API Error:', err)
        throw err
      }
    }

    function logout() {
      profileAuthInfo.value = null
      patientAssociated.value = null
      currentPatient.value = null
    }

    return {
      facilityKey,
      intakeKey,
      profileAuthInfo,
      patientAssociated,
      currentPatient,
      facilityInfo,
      linkInfo,
      currentProfilePageView,
      currentProfilePaymentView,
      setFacilityKey,
      setIntakeKey,
      setProfileAuthInfo,
      setProfileMyInfo,
      setPatientAssociated,
      setCurrentPatient,
      patchCurrentPatient,
      setFacilityInfo,
      setLinkInfo,
      setProfileCurrentPage,
      setProfilePaymentCurrentPage,
      getLink,
      logout
    }
  },
  { zustandPersist: { key: 'portal-profile' } }
)
